/**
 * Phase 6 operator-workflow schema. Per CD spec, the schema this implies:
 *
 *   per-user:    lastVisit (ISO timestamp)
 *   inbox:       state per item (new / ack / snoozed / dismissed)
 *   rules[]:     each carrying a `kind: 'real' | 'quality'` discriminator
 *                so the operator knows whether a fire was a real-world
 *                event or a data-quality event (different action)
 *   pins[]:      TYPED (country / outbreak / signal / pathogen) — different
 *                decay rates per type
 *   annotations[]: per-user, local-only, markdown body
 *
 * Honesty rails (non-negotiable per CD):
 *   - Change-types distinguish real-world vs data-quality events
 *   - Pins to stale data MUST visibly flag the staleness
 *   - Annotations are LOCAL-ONLY by default; the "local-only" lock badge
 *     is unmissable
 *   - Briefings carry per-item source + asOf AND the EDU disclaimer
 *     embedded in the EXPORT (not just the chrome)
 *   - Alerts must distinguish "fired because data updated" vs "fired
 *     because source went DEGRADED" — different operator action
 */

/** Three operator personas — drive Inbox sort/filter defaults + Alert
 *  rule-builder defaults + Comparison-mode default axes. All controls
 *  remain available to everyone regardless of persona. */
export type Persona = 'who' | 'cmo' | 'usg';

/** Threat tier — matches the surveillance-feed `signalThreatTier()` output. */
export type ThreatTier = 0 | 1 | 2 | 3 | 4;

/** Inbox change-type vocabulary — the honesty core. `real` events are real-
 *  world signals; quality events are data-pipeline (degraded source, stale
 *  feed) and demand a DIFFERENT operator action ("check the feed, not the
 *  outbreak"). Carried by colour + icon + word; never colour alone. */
export type ChangeType = 'escalation' | 'new' | 'degraded' | 'coverage' | 'deescalation';

/** Per-Inbox-item state. Persists per-user, drives the "since last visit"
 *  divider + the "needs attention" group. */
export type InboxState = 'new' | 'ack' | 'snoozed' | 'dismissed';

/** Pin types — different decay rates per type (per CD spec):
 *  country = slow decay (institutional interest)
 *  outbreak = medium (event-bounded)
 *  signal = fast (per-datum, ages out within days)
 *  pathogen = slow (broad watch) */
export type PinType = 'country' | 'outbreak' | 'signal' | 'pathogen';

/** Alert-rule discriminator — `real` rules fire on world events, `quality`
 *  rules fire on data-pipeline state changes (DEGRADED / ROTTING / etc.). */
export type RuleKind = 'real' | 'quality';

/** Pathogen family (mirrors the surveillance-feed enum). */
export type PathogenFamily = 'haemorrhagic' | 'zoonotic' | 'respiratory' | 'enteric' | 'vector';

/** A single inbox item — one change that fired into the operator's view. */
export interface InboxItem {
  id: string;
  /** Pathogen the change concerns (catalog id; e.g. 'ebola', 'mpox'). */
  pathogen: string;
  /** Pathogen common name (display). */
  common: string;
  family: PathogenFamily;
  /** Identity colour CSS-var token (e.g. 'var(--bm-id-7)'). */
  idc: string;
  admin: string;
  iso3: string;
  /** WHO region — drives persona filtering (AFRO / AMRO / EMRO / EURO / SEARO / WPRO). */
  region: string;
  tier: ThreatTier;
  change: ChangeType;
  /** Short prose describing the change ("Cases 112 → 142 (+27%) in 48h"). */
  detail: string;
  /** ISO timestamp of when the change fired. */
  ts: string;
  /** Source id that produced the change. */
  source: string;
  /** ISO timestamp the underlying signal was asOf. */
  asOf: string;
  state: InboxState;
  /** Optional flags surfaced inline. */
  covGap?: boolean;
  novel?: boolean;
  unverified?: boolean;
  /** When true, this item is a DATA-QUALITY event (not a real-world change).
   *  Mirrors `CHANGE[change].real === false` but explicit for query speed. */
  dataQuality?: boolean;
}

/** An alert rule — declarative condition list + notification channels. */
export interface AlertRuleCondition {
  /** Field path being tested (e.g. 'tier', 'pathogen', 'source.state'). */
  f: string;
  /** Operator ('=', '≥', '>', '<', 'in'). */
  op: string;
  /** Value (string at the schema level; type per field). */
  v: string;
}

export interface AlertRule {
  id: string;
  name: string;
  on: boolean;
  starter: boolean;
  when: AlertRuleCondition[];
  notify: Array<'inbox' | 'email' | 'push' | 'webhook'>;
  /** Backtest: how often this rule would have fired in the last 7 days. */
  firedLastWk: number;
  kind: RuleKind;
}

/** A pin in a watchlist. Different `type` → different decay (CD spec). */
export interface Pin {
  id: string;
  type: PinType;
  label: string;
  pathogen: string;
  tier: ThreatTier;
  /** True when the pinned datum has aged past freshness threshold —
   *  MUST visibly flag (per CD spec honesty rail). */
  stale: boolean;
  /** Age in days (drives the stale flag + tooltip text). */
  asOfDays: number;
}

export interface Watchlist {
  id: string;
  name: string;
  pins: Pin[];
}

/** An annotation — per-user, local-only, markdown body. */
export interface Annotation {
  id: string;
  /** Label identifying what this annotation is about ("Ebola · North Kivu"). */
  target: string;
  pathogen: string;
  /** Markdown body (subset: **bold**, _italic_, - bullet, plain paragraphs). */
  body: string;
  /** ISO timestamp of last edit. */
  edited: string;
  /** Days since last touched — drives the decay flag. */
  touchedDays: number;
  /** Set when touchedDays exceeds the decay threshold (e.g. 7 days). */
  decayed?: boolean;
}

/** One outbreak trajectory comparable in the Comparison mode. */
export interface Comparable {
  id: string;
  label: string;
  sub: string;
  pathogen: string;
  family?: PathogenFamily;
  tier: ThreatTier;
  /** CSS-var colour for the trace. */
  color: string;
  /** Daily incidence-ish series. */
  data: number[];
  /** Day-of-peak (for the readout). */
  peakDay: number;
  /** Unit label ("cases/day"). */
  metric: string;
  /** True when this trace is a reference baseline (dashed; not a comparable per se). */
  baseline?: boolean;
}

/** Persona display metadata — keyed by Persona id. */
export const PERSONA_META: Record<Persona, {
  name: string;
  short: string;
  scope: string;
  cares: string;
}> = {
  who: {
    name: 'Duty epidemiologist · WHO regional',
    short: 'WHO duty',
    scope: 'AFRO + EMRO',
    cares: 'cross-border spread, under-reported events, WHO-region rollups',
  },
  cmo: {
    name: 'Deputy CMO · national',
    short: 'Nat. CMO',
    scope: 'single country, sub-national',
    cares: 'their jurisdiction, public messaging (coverage!), ministerial briefings',
  },
  usg: {
    name: 'Analyst · USG / all-source',
    short: 'USG analyst',
    scope: 'global, all-source',
    cares: 'novel pathogens, genomic, source credibility, the global picture',
  },
};

/** Change-type display metadata. `real` flag distinguishes real-world from
 *  data-quality events. */
export const CHANGE_META: Record<ChangeType, {
  word: string;
  icon: string;
  cssVar: string;
  bgVar: string;
  real: boolean;
}> = {
  escalation: { word: 'Escalated', icon: '▲', cssVar: 'var(--bm-chg-escalation)', bgVar: 'var(--bm-chg-escalation-bg)', real: true },
  new: { word: 'New detection', icon: '✦', cssVar: 'var(--bm-chg-new)', bgVar: 'var(--bm-chg-new-bg)', real: true },
  degraded: { word: 'Source degraded', icon: '◇', cssVar: 'var(--bm-chg-degraded)', bgVar: 'var(--bm-chg-degraded-bg)', real: false },
  coverage: { word: 'Coverage gap', icon: '◍', cssVar: 'var(--bm-chg-coverage)', bgVar: 'var(--bm-chg-coverage-bg)', real: true },
  deescalation: { word: 'Improving', icon: '▼', cssVar: 'var(--bm-chg-deescalation)', bgVar: 'var(--bm-chg-deescalation-bg)', real: true },
};

/** Pin-type display metadata. */
export const PIN_TYPE_META: Record<PinType, { word: string; icon: string; decay: 'slow' | 'medium' | 'fast' }> = {
  country: { word: 'Country', icon: '▣', decay: 'slow' },
  outbreak: { word: 'Outbreak', icon: '◆', decay: 'medium' },
  signal: { word: 'Signal', icon: '•', decay: 'fast' },
  pathogen: { word: 'Pathogen', icon: '✛', decay: 'slow' },
};

/** Threat tier display metadata (mirrors threatTier.ts; lifted here so
 *  Phase 6 components don't depend on the existing tier infrastructure for
 *  their MVP-ish demo state). */
export const TIER_META: Record<ThreatTier, { word: string; cssVar: string; bgVar: string }> = {
  0: { word: 'ROUTINE', cssVar: 'var(--bm-threat-0)', bgVar: 'var(--bm-threat-0-bg)' },
  1: { word: 'CRITICAL', cssVar: 'var(--bm-threat-1)', bgVar: 'var(--bm-threat-1-bg)' },
  2: { word: 'HIGH', cssVar: 'var(--bm-threat-2)', bgVar: 'var(--bm-threat-2-bg)' },
  3: { word: 'ELEVATED', cssVar: 'var(--bm-threat-3)', bgVar: 'var(--bm-threat-3-bg)' },
  4: { word: 'WATCH', cssVar: 'var(--bm-threat-4)', bgVar: 'var(--bm-threat-4-bg)' },
};
